import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Trophy } from '@sorare/core/src/atoms/icons/Trophy';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType, isTypes } from '@sorare/core/src/lib/gql';
import { SetBrandingImage } from '@sorare/core/src/set/branding/SetBrandingImage.tsx';
import { useCurrentSetIcon } from '@sorare/core/src/set/branding/useCurrentSetIcon.ts';

import { MissionSection } from '#src/components/missions/MissionSection/index.tsx';
import { SetCollectionTask } from '#src/components/missions/SetCollectionTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { CollectionMissions_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

const SET_ICON_COLOR = 'var(--c-inventory-yellow)';

const hasCompetition = (task: CollectionMissions_taskInterface) => {
  if (!isType(task, 'TasksTrack')) {
    return false;
  }
  const step = task.currentStep;
  if (
    !isTypes(step, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ])
  ) {
    return false;
  }
  return step.competitions.some(({ slug }) => !!slug);
};

type Props = {
  tasks: CollectionMissions_taskInterface[];
  onRefetch?: () => void;
  onTaskClick: (taskId: string) => void;
  selectedTaskId?: Nullable<string>;
};

export const CollectionMissions = ({
  tasks,
  onRefetch,
  onTaskClick,
  selectedTaskId,
}: Props) => {
  const setIconUrl = useCurrentSetIcon();
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  if (!tasks.length) {
    return null;
  }

  const claimNowTasks = tasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );
  const inProgressLeagueTasks = tasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      hasCompetition(task)
  );
  const inProgressGeneralTasks = tasks.filter(
    task =>
      (task.aasmState === ManagerProgressionTaskState.READY ||
        task.aasmState === ManagerProgressionTaskState.ASSIGNED) &&
      !hasCompetition(task)
  );
  const completedTasks = tasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  const renderTasks = (taskList: typeof tasks) =>
    taskList.map(task => (
      <SetCollectionTask
        key={task.id}
        task={task}
        onClaim={async () => {
          const currentStep = isType(task, 'TasksTrack')
            ? task.currentStep
            : null;
          if (currentStep) {
            onClaim(currentStep);
          }
        }}
        loading={loadingTaskId === task.id}
        onClick={() => onTaskClick(task.id)}
        selected={
          selectedTaskId ===
          (isType(task, 'TasksTrack') ? task.currentStep?.id : undefined)
        }
      />
    ));

  return (
    <>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          onRefetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          onRefetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          onRefetch?.();
        }}
      />
      <Root>
        {claimNowTasks.length > 0 && (
          <MissionSection
            icon={<FontAwesomeIcon icon={iconGift} />}
            iconColor={SET_ICON_COLOR}
            title={<FormattedMessage defaultMessage="Claim Now" />}
          >
            {renderTasks(claimNowTasks)}
          </MissionSection>
        )}
        {inProgressLeagueTasks.length > 0 && (
          <MissionSection
            icon={<Trophy size={2} />}
            iconColor={SET_ICON_COLOR}
            title={<FormattedMessage defaultMessage="League" />}
          >
            {renderTasks(inProgressLeagueTasks)}
          </MissionSection>
        )}
        {inProgressGeneralTasks.length > 0 && (
          <MissionSection
            icon={<SetBrandingImage url={setIconUrl} size={2} />}
            iconColor={SET_ICON_COLOR}
            title={<FormattedMessage defaultMessage="General" />}
          >
            {renderTasks(inProgressGeneralTasks)}
          </MissionSection>
        )}
        {completedTasks.length > 0 && (
          <MissionSection
            icon={<FontAwesomeIcon icon={iconCheck} />}
            iconColor={SET_ICON_COLOR}
            title={<FormattedMessage defaultMessage="Completed" />}
          >
            {renderTasks(completedTasks)}
          </MissionSection>
        )}
      </Root>
    </>
  );
};

CollectionMissions.fragments = {
  taskInterface: gql`
    fragment CollectionMissions_taskInterface on TaskInterface {
      id
      aasmState
      ... on TasksTrack {
        id
        currentStep {
          id
          ... on CardsCountTargetTask {
            id
            competitions {
              slug
            }
          }
          ... on CompleteCollectionsCountTargetTask {
            id
            competitions {
              slug
            }
          }
          ...TaskClaimedDialogs_taskInterface
        }
      }
      ...SetCollectionTask_taskInterface
    }
    ${TaskClaimedDialogs.fragments.taskInterface}
    ${SetCollectionTask.fragments.taskInterface}
  ` as TypedDocumentNode<CollectionMissions_taskInterface>,
};
